/**
 * The single HTTP door to the KK POS API.
 *
 * Architecture note — why there is no BFF here:
 * the API signs a `kk-admin-token` HttpOnly cookie (SameSite=strict, path=/,
 * 1h) and `@fastify/jwt` is configured to read the admin JWT straight out of
 * that cookie. So as long as the console is served from the SAME ORIGIN as
 * `/api` (vite proxy in dev, `server/index.js` in prod) the browser attaches
 * credentials by itself and no token ever touches JavaScript. That is both
 * safer and simpler than forwarding a bearer token through a server layer.
 *
 * Everything else in the app goes through `api.get/post/patch/put/del`.
 */

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

/** Typed superset of the API's standard error envelope. Lifecycle routes add
 *  the optional conflict fields beside `error`. */
export interface ApiErrorBody {
  success?: boolean;
  error?: string;
  /** A stable refusal code beside the sentence (`COUNT_CONFIRMATION_REQUIRED`, …). */
  code?: string;
  message?: string;
  details?: JsonValue;
  reason?: string;
  currentStatus?: string;
  recoveredTransactionId?: string | null;
}

export class ApiError extends Error {
  readonly status: number;
  readonly details?: JsonValue;
  /** The parsed JSON error envelope, when there was one. Some routes attach
   *  machine-readable fields beside `error` (`reason`, `currentStatus`,
   *  `recoveredTransactionId` on a lifecycle 409) that a screen may branch on. */
  readonly body?: ApiErrorBody;
  /** Set when the failure was a network/timeout problem, not an HTTP status. */
  readonly offline: boolean;

  constructor(
    message: string,
    opts: {
      status: number;
      details?: JsonValue;
      body?: ApiErrorBody;
      offline?: boolean;
    }
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = opts.status;
    this.details = opts.details;
    this.body = opts.body;
    this.offline = opts.offline ?? false;
  }

  /** 401/403 — the operator is signed out or lacks the capability. */
  get isAuth() {
    return this.status === 401 || this.status === 403;
  }

  get isForbidden() {
    return this.status === 403;
  }

  /** 409/422 — a business-rule refusal worth showing verbatim to the operator. */
  get isConflict() {
    return this.status === 409 || this.status === 422;
  }
}

export type QueryParams = Record<
  string,
  string | number | boolean | null | undefined | Array<string | number>
>;

export interface RequestOptions {
  params?: QueryParams;
  signal?: AbortSignal;
  /** Milliseconds before the request is aborted. Default 30s. */
  timeout?: number;
  headers?: Record<string, string>;
  /** Skip the automatic refresh-and-retry dance (used by auth calls). */
  noRefresh?: boolean;
  /** Expect a non-JSON body (CSV/PDF export). Resolves to a Blob. */
  raw?: boolean;
}

const DEFAULT_TIMEOUT = 30_000;

/** Build `?a=1&b=x` from a params object, dropping null/undefined and
 *  expanding arrays into repeated keys (which is what the API expects). */
export function buildQuery(params?: QueryParams): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue;
    if (Array.isArray(value)) {
      for (const v of value) sp.append(key, String(v));
    } else {
      sp.append(key, String(value));
    }
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

/* ── Session-expiry handling ─────────────────────────────────────────────────
   The access cookie lives one hour; the refresh cookie thirty days. On the
   first 401 we try exactly one refresh and replay the request. Concurrent
   401s share a single in-flight refresh so a dashboard firing eight queries
   at once does not fire eight refreshes. If the refresh fails, we hand control
   to whoever registered `onSessionExpired` (the router, which redirects to
   /login preserving the current URL).                                        */

let refreshInFlight: Promise<boolean> | null = null;
let sessionExpiredHandler: (() => void) | null = null;

export function onSessionExpired(handler: () => void) {
  sessionExpiredHandler = handler;
}

/**
 * Mint a fresh access cookie from the HttpOnly refresh cookie.
 *
 * Exported because the assist WebSocket needs the *same* single-flight
 * refresh: its handshake reads the same `kk-admin-token` cookie, so when the
 * hub rejects it at the 1h mark the socket must join this promise rather than
 * firing a competing refresh of its own.
 *
 * Never throws — resolves false on any failure.
 */
export async function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        // With no body token, the API derives auth from the HttpOnly refresh
        // cookie and never reflects a bearer token into browser-visible JSON.
        body: '{}',
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      // Cleared on the next microtask so all callers awaiting this exact
      // promise observe the same result before a new attempt can start.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();
  return refreshInFlight;
}

async function toApiError(res: Response): Promise<ApiError> {
  const text = await res.text().catch(() => '');
  if (!text) {
    return new ApiError(res.statusText || `Request failed (${res.status})`, {
      status: res.status,
    });
  }
  try {
    const parsed = JSON.parse(text) as ApiErrorBody;
    const message = parsed.error ?? parsed.message ?? res.statusText ?? 'Request failed';
    return new ApiError(message, {
      status: res.status,
      details: parsed.details,
      body: parsed,
    });
  } catch {
    return new ApiError(text.slice(0, 300), { status: res.status });
  }
}

/** Unwrap the API's `{ success, data }` envelope. Endpoints that predate the
 *  envelope return a bare object; both shapes are accepted. */
function unwrap<T>(json: unknown): T {
  if (json && typeof json === 'object' && 'success' in json) {
    const env = json as { success: boolean; data?: T; error?: string };
    if (env.success === false) {
      throw new ApiError(env.error ?? 'Request failed', { status: 400 });
    }
    return env.data as T;
  }
  return json as T;
}

/**
 * The single door through which every request actually leaves the app.
 *
 * One attempt; on a 401, exactly one refresh and exactly one replay. There is
 * no recursion and no retry counter to get wrong — the replay physically
 * cannot 401-loop because it is a straight-line second call.
 *
 * Every caller (`request` and `api.download`) goes through here so the
 * refresh path cannot be forgotten by a new helper — that is precisely how
 * `api.download` ended up bypassing it.
 *
 * Replay safety (see TASK-0170 / AGENTS.md money rules): the replay re-sends
 * the request verbatim, including POST/DELETE. That is only safe because the
 * API emits every 401 from a preHandler or a top-of-handler identity guard,
 * before any DB write. Do not widen what reaches this function.
 */
async function send(
  url: string,
  init: RequestInit,
  opts: { noRefresh?: boolean; callerSignal?: AbortSignal } = {}
): Promise<Response> {
  const attempt = async (): Promise<Response> => {
    try {
      return await fetch(url, init);
    } catch (err) {
      const aborted = err instanceof DOMException && err.name === 'AbortError';
      // A caller-initiated abort (component unmounted, query superseded) must
      // propagate untouched so TanStack Query can discard it silently.
      if (aborted && opts.callerSignal?.aborted) throw err;
      throw new ApiError(
        aborted ? 'The server took too long to respond.' : 'Cannot reach the server.',
        { status: 0, offline: true }
      );
    }
  };

  const res = await attempt();
  if (res.status !== 401 || opts.noRefresh) return res;

  if (await refreshSession()) {
    const replayed = await attempt();
    // A 401 that survives a *successful* refresh is not a transient expiry —
    // the account was disabled, or the cookie is being dropped. Bounce rather
    // than surfacing a bare "Unauthorized" and leaving the console wedged.
    if (replayed.status !== 401) return replayed;
  }

  sessionExpiredHandler?.();
  throw new ApiError('Your session has expired. Sign in again.', { status: 401 });
}

/** Compose the caller's abort signal with a timeout, if either is wanted. */
function composeSignal(
  callerSignal: AbortSignal | undefined,
  timeoutMs: number | undefined
): AbortSignal | undefined {
  if (timeoutMs === undefined) return callerSignal;
  const timeoutSignal = AbortSignal.timeout(timeoutMs);
  return callerSignal ? AbortSignal.any([callerSignal, timeoutSignal]) : timeoutSignal;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestOptions = {}
): Promise<T> {
  const url = `/api${path.startsWith('/') ? path : `/${path}`}${buildQuery(options.params)}`;

  const signal = composeSignal(options.signal, options.timeout ?? DEFAULT_TIMEOUT);

  const isFormData = body instanceof FormData;
  const headers: Record<string, string> = {
    accept: options.raw ? '*/*' : 'application/json',
    ...options.headers,
  };
  // FormData must set its own multipart boundary — never override it.
  if (body !== undefined && !isFormData) headers['content-type'] = 'application/json';

  const res = await send(
    url,
    {
      method,
      credentials: 'same-origin',
      headers,
      signal,
      body: body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
    },
    { noRefresh: options.noRefresh, callerSignal: options.signal }
  );

  if (!res.ok) throw await toApiError(res);

  if (options.raw) return (await res.blob()) as T;
  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }

  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    return (await res.text()) as T;
  }

  return unwrap<T>(await res.json());
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>('GET', path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('POST', path, body, options),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PUT', path, body, options),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PATCH', path, body, options),
  del: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('DELETE', path, body, options),
  /**
   * Download an export. Returns a Blob plus the server's filename, if any.
   *
   * Goes through `send`, so a report CSV or receipt PDF pulled at the 1h mark
   * survives the access-cookie expiry instead of failing in the operator's
   * face. It is a GET by construction — the replay-safety note on `send`
   * depends on downloads staying read-shaped.
   *
   * Unlike `request`, there is deliberately no default timeout: a year-wide
   * report legitimately takes a while, and the old bare fetch had none either.
   * Callers that want one pass `timeout`.
   */
  download: async (path: string, options?: RequestOptions) => {
    const url = `/api${path.startsWith('/') ? path : `/${path}`}${buildQuery(options?.params)}`;
    const res = await send(
      url,
      {
        method: 'GET',
        credentials: 'same-origin',
        headers: { accept: '*/*', ...options?.headers },
        signal: composeSignal(options?.signal, options?.timeout),
      },
      { noRefresh: options?.noRefresh, callerSignal: options?.signal }
    );
    if (!res.ok) throw await toApiError(res);
    const disposition = res.headers.get('content-disposition') ?? '';
    const match = /filename="?([^"';]+)"?/i.exec(disposition);
    return { blob: await res.blob(), filename: match?.[1] ?? 'export.csv' };
  },
};

/** Trigger a browser save for a Blob produced by `api.download`. */
export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next frame — revoking synchronously can cancel the download
  // in Safari before it has read the object.
  requestAnimationFrame(() => URL.revokeObjectURL(url));
}

/** Human-readable text for any thrown value, for toasts and inline errors. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong.';
}
